import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Send,
  Sparkles,
  ShieldCheck,
  Loader2,
  ArrowLeft,
  MessageSquare,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";
import { getChatSummary, getMessages, askQuestion } from "../lib/chat";
import { formatApiError } from "../lib/authApi";

export default function Chat() {
  const { uploadId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [summary, setSummary] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [pageError, setPageError] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setPageError("");
    try {
      const [s, m] = await Promise.all([
        getChatSummary(uploadId),
        getMessages(uploadId),
      ]);
      setSummary(s);
      setMessages(m);
    } catch (e) {
      setPageError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [uploadId]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate(`/ask?redirect=${encodeURIComponent(`/chat/${uploadId}`)}`);
      return;
    }
    if (user) load();
  }, [authLoading, user, uploadId, load, navigate]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    setBusy(true);
    // Optimistically show the user's message.
    const optimistic = {
      id: `opt-${Date.now()}`,
      role: "user",
      content: q,
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, optimistic]);
    try {
      const res = await askQuestion(uploadId, q);
      setMessages((m) => {
        const withoutOpt = m.filter((x) => x.id !== optimistic.id);
        return [...withoutOpt, res.question, res.answer];
      });
    } catch (e) {
      setMessages((m) => [
        ...m.filter((x) => x.id !== optimistic.id),
        {
          id: `err-${Date.now()}`,
          role: "assistant",
          content:
            "Sorry — I couldn't answer that just now. Please try again in a moment.",
          created_at: new Date().toISOString(),
          _error: true,
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const empty = messages.length === 0;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />

      <div
        className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 lg:px-8 pt-6 md:pt-8 pb-4 flex flex-col"
        data-testid="chat-page"
      >
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 self-start"
          data-testid="chat-back-btn"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        {loading ? (
          <div
            className="flex-1 flex items-center justify-center text-slate-500 text-sm"
            data-testid="chat-loading"
          >
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Loading your policy…
          </div>
        ) : pageError ? (
          <div
            className="mt-6 border border-red-200 rounded-2xl p-8 bg-red-50 text-center"
            data-testid="chat-page-error"
          >
            <p className="text-red-800 font-medium">{pageError}</p>
            <Link
              to="/dashboard"
              className="mt-4 inline-flex items-center gap-1.5 text-sm text-red-700 hover:text-red-900 underline underline-offset-4"
            >
              Back to dashboard
            </Link>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="mt-4 pb-6 border-b border-slate-200">
              <div className="flex items-start justify-between gap-4 flex-col md:flex-row">
                <div>
                  <h1
                    className="font-display text-2xl md:text-3xl tracking-tight text-slate-900"
                    data-testid="chat-title"
                  >
                    {summary?.insurer && summary.insurer !== "not specified in your policy"
                      ? summary.insurer
                      : "Your policy"}
                    {summary?.vehicle && summary.vehicle !== "not specified in your policy"
                      ? ` — ${summary.vehicle}`
                      : ""}
                  </h1>
                  <p className="text-sm text-slate-500 mt-1">
                    Answers use only your uploaded policy text.
                  </p>
                </div>
                <Link
                  to={`/policy/${uploadId}`}
                  data-testid="chat-view-analysis"
                  className="inline-flex items-center gap-1.5 text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-full px-3 py-1.5 font-medium"
                >
                  View analysis
                </Link>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 py-6" data-testid="chat-body">
              {empty ? (
                <div
                  className="flex flex-col items-center text-center py-6"
                  data-testid="chat-empty-state"
                >
                  <div className="h-14 w-14 rounded-2xl bg-blue-600 text-white flex items-center justify-center shadow-lg shadow-blue-600/20">
                    <MessageSquare className="h-6 w-6" />
                  </div>
                  <h2 className="mt-6 font-display text-xl font-semibold text-slate-900">
                    Ask anything about this policy
                  </h2>
                  <p className="mt-2 text-slate-600 max-w-md text-sm">
                    I&apos;ll answer using only the actual text of your uploaded
                    document. If it&apos;s not in there, I&apos;ll tell you.
                  </p>

                  {summary?.suggested_questions?.length > 0 && (
                    <div className="mt-8 w-full max-w-2xl">
                      <div className="text-xs uppercase tracking-widest text-slate-400 font-medium mb-3">
                        Suggested starters
                      </div>
                      <div
                        className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                        data-testid="suggested-questions"
                      >
                        {summary.suggested_questions.map((q, i) => (
                          <button
                            key={q}
                            onClick={() => send(q)}
                            data-testid={`suggested-q-${i}`}
                            className="chip-suggest text-left bg-white border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 rounded-2xl p-4 text-sm text-slate-700"
                          >
                            {q}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4" data-testid="chat-messages">
                  {messages.map((m) => (
                    <div
                      key={m.id}
                      className={`flex ${
                        m.role === "user" ? "justify-end" : "justify-start"
                      }`}
                      data-testid={`msg-${m.role}`}
                    >
                      <div
                        className={`max-w-[82%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                          m.role === "user"
                            ? "bg-blue-600 text-white rounded-br-sm"
                            : m._error
                            ? "bg-red-50 text-red-800 border border-red-100 rounded-bl-sm"
                            : "bg-slate-100 text-slate-800 rounded-bl-sm"
                        }`}
                      >
                        {m.content}
                      </div>
                    </div>
                  ))}
                  {busy && (
                    <div className="flex justify-start" data-testid="chat-thinking">
                      <div className="bg-slate-100 rounded-2xl rounded-bl-sm px-4 py-3 text-sm text-slate-500 inline-flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Reading your policy…
                      </div>
                    </div>
                  )}
                  <div ref={endRef} />
                </div>
              )}
            </div>

            {/* Composer */}
            <div className="sticky bottom-0 pb-6 pb-[calc(env(safe-area-inset-bottom)+1.5rem)] bg-gradient-to-t from-white via-white to-transparent pt-4">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  send();
                }}
                className="flex items-center gap-2 bg-white border border-slate-200 rounded-full pl-5 pr-1.5 py-1.5 shadow-sm hover:shadow-md focus-within:border-blue-400"
                data-testid="chat-form"
              >
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about coverage, exclusions, add-ons…"
                  className="flex-1 bg-transparent outline-none text-sm text-slate-800 placeholder:text-slate-400 py-2.5"
                  data-testid="chat-input"
                  disabled={busy}
                />
                <button
                  type="submit"
                  disabled={!input.trim() || busy}
                  data-testid="chat-send-btn"
                  className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-full px-4 py-2.5 text-sm font-medium"
                >
                  Ask
                  <Send className="h-3.5 w-3.5" />
                </button>
              </form>
              <p className="mt-3 text-xs text-slate-500 text-center flex items-start justify-center gap-1.5 max-w-lg mx-auto leading-relaxed" data-testid="chat-inline-disclaimer">
                <ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>
                  <strong className="font-medium text-slate-600">
                    Answers are AI-generated from your policy text and may be wrong.
                  </strong>{" "}
                  Confirm with your insurer before acting.
                </span>
              </p>
            </div>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}
